import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import { injectTracking } from "./lib/seo";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { SessionExpiredModal } from "./components/SessionExpiredModal";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import Categories from "./pages/Categories";
import Labels from "./pages/Labels";
import Sales from "./pages/Sales";
import SalesHistory from "./pages/SalesHistory";
import Settings from "./pages/Settings";
import Orders from "./pages/Orders";
import Reviews from "./pages/Reviews";
import Customers from "./pages/Customers";
import Notifications from "./pages/Notifications";
import Conversations from "./pages/Conversations";
import Activity from "./pages/Activity";
import PublicInvoice from "./pages/PublicInvoice";
import PublicVitrine from "./pages/PublicVitrine";
import PublicOrderChat from "./pages/PublicOrderChat";
import PublicReview from "./pages/PublicReview";
import CustomerLayout from "./components/CustomerLayout";
import CustomerAuth from "./pages/CustomerAuth";
import CustomerFavorites from "./pages/CustomerFavorites";
import CustomerOrders from "./pages/CustomerOrders";
import CustomerMessages from "./pages/CustomerMessages";
import CustomerProfile from "./pages/CustomerProfile";
import CustomerCart from "./pages/CustomerCart";
import BoutiqueOSWelcome from "./pages/BoutiqueOSWelcome";
import BoutiqueOSOnboarding from "./pages/BoutiqueOSOnboarding";
import Team from "./pages/Team";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminLayout from "./components/SuperAdminLayout";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import SuperAdminBoutiques from "./pages/SuperAdminBoutiques";
import SuperAdminPayments from "./pages/SuperAdminPayments";
import SuperAdminSettings from "./pages/SuperAdminSettings";
import VendeurSettings from "./pages/VendeurSettings";
import ServiceOrders from "./pages/ServiceOrders";
import Tables from "./pages/Tables";
import TablesQR from "./pages/TablesQR";
import TableClient from "./pages/TableClient";
import Cancellations from "./pages/Cancellations";
import Layout from "./components/Layout";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionExpiredRef = useRef(false);

  // [SESSION-UNIQUE] Clear all auth state
  const clearAuth = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    sessionStorage.removeItem("currentUser");
    sessionStorage.removeItem("compteValide");
    setToken(null);
    setUser(null);
  }, []);

  // [SESSION-UNIQUE] Setup axios interceptor for SESSION_REPLACED detection
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 401 &&
          error.response?.data?.detail === "SESSION_REPLACED" &&
          !sessionExpiredRef.current
        ) {
          sessionExpiredRef.current = true;
          setSessionExpired(true);
          clearAuth();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [clearAuth]);

  const handleSessionExpiredClose = useCallback(() => {
    setSessionExpired(false);
    sessionExpiredRef.current = false;
    window.location.href = "/";
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          // [TEAM-SYNC] Ensure userRole stays synced on refresh
          if (response.data.role && response.data.team_member_id) {
            localStorage.setItem("userRole", response.data.role);
          }
        } catch (error) {
          // SESSION_REPLACED is handled by the interceptor
          if (error.response?.data?.detail !== "SESSION_REPLACED") {
            localStorage.removeItem("token");
            setToken(null);
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (identifier, password) => {
    const response = await axios.post(`${API}/auth/login`, { identifiant: identifier, password });
    const { access_token, user: userData, boutique, compteValide } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return { user: userData, boutique, compteValide };
  };

  // [TEAM-SYNC] Store JWT token for team members
  const setTeamAuth = (accessToken, memberData) => {
    localStorage.setItem("token", accessToken);
    setToken(accessToken);
    setUser({
      id: memberData.id,
      name: `${memberData.prenom} ${memberData.nom}`.trim(),
      email: memberData.email,
      role: memberData.role,
      team_member_id: memberData.id,
    });
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearAuth();
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full tropical-gradient"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, getAuthHeaders, setTeamAuth }}>
      {children}
      {sessionExpired && <SessionExpiredModal onClose={handleSessionExpiredClose} />}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Allow access if owner (JWT user) OR team member (sessionStorage currentUser)
  const sessionUser = (() => {
    try { return JSON.parse(sessionStorage.getItem("currentUser") || "null"); }
    catch { return null; }
  })();

  if (!user && !sessionUser) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user } = useAuth();

  if (user) {
    // [TEAM-SYNC] Redirect based on role
    const role = user.role || localStorage.getItem("userRole") || "owner";
    if (role === "vendeur") {
      return <Navigate to="/admin/sales" replace />;
    }
    if (role === "serveur") {
      return <Navigate to="/admin/service" replace />;
    }
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* BoutiqueOS Welcome Screen - Landing Page */}
      <Route path="/" element={<BoutiqueOSWelcome />} />
      <Route path="/welcome" element={<BoutiqueOSWelcome />} />
      <Route path="/onboarding" element={<BoutiqueOSOnboarding />} />
      
      {/* Login redirige vers page d'accueil (modal) */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/admin-login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/invoice/:invoiceId" element={<PublicInvoice />} />
      <Route path="/vitrine/:userId" element={<PublicVitrine />} />
      <Route path="/t/:token" element={<TableClient />} />
      <Route path="/vitrine/:userId/login" element={<CustomerAuth />} />
      <Route path="/vitrine/:userId/espace" element={<CustomerLayout />}>
        <Route index element={<Navigate to="favorites" replace />} />
        <Route path="favorites" element={<CustomerFavorites />} />
        <Route path="orders" element={<CustomerOrders />} />
        <Route path="messages" element={<CustomerMessages />} />
        <Route path="profile" element={<CustomerProfile />} />
        <Route path="cart" element={<CustomerCart />} />
      </Route>
      <Route path="/order/:orderId" element={<PublicOrderChat />} />
      <Route path="/review/:orderId" element={<PublicReview />} />
      <Route path="/admin" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="sales" element={<Sales />} />
        <Route path="service" element={<ServiceOrders />} />
        <Route path="tables" element={<Tables />} />
        <Route path="tables/qr" element={<TablesQR />} />
        <Route path="cancellations" element={<Cancellations />} />
        <Route path="history" element={<SalesHistory />} />
        <Route path="products" element={<Products />} />
        <Route path="categories" element={<Categories />} />
        <Route path="labels" element={<Labels />} />
        <Route path="orders" element={<Orders />} />
        <Route path="customers" element={<Customers />} />
        <Route path="notifications" element={<Notifications />} />
        {/* [CONV-1-SYSTEME] Conversations route */}
        <Route path="conversations" element={<Conversations />} />
        <Route path="reviews" element={<Reviews />} />
        <Route path="team" element={<Team />} />
        <Route path="activity" element={<Activity />} />
        <Route path="settings" element={<Settings />} />
        <Route path="mon-profil" element={<VendeurSettings />} />
      </Route>
      
      {/* [SUPERADMIN-1-STATS] Routes Super Admin */}
      <Route path="/super-admin" element={<SuperAdminLogin />} />
      <Route path="/super-admin" element={<SuperAdminLayout />}>
        <Route path="dashboard" element={<SuperAdminDashboard />} />
        <Route path="boutiques" element={<SuperAdminBoutiques />} />
        <Route path="payments" element={<SuperAdminPayments />} />
        <Route path="settings" element={<SuperAdminSettings />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // [SEO/SEA] Tracking plateforme (optionnel) — IDs via variables d'environnement
  useEffect(() => {
    injectTracking({
      ga4_id: process.env.REACT_APP_GA4_ID,
      google_ads_id: process.env.REACT_APP_GOOGLE_ADS_ID,
      meta_pixel_id: process.env.REACT_APP_META_PIXEL_ID,
    });
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
